/* eslint-disable no-console */
const logger = require("pino")({ name: "useAudioPlayer", level: "info" });

let source;
let queue = [];
let audioElements = {};

function isPlaying(audioElement) {
  if (!audioElement) {
    return false;
  } else {
    return !audioElement.paused;
  }
}

export default function useAudioPlayer() {
  const play = async (binary, callback, highlightCallback, onPlaying) => {
    const audioResult =
      binary.startsWith("http") || binary.startsWith("blob")
        ? binary
        : `data:audio/x-wav;base64, ${binary}`;

    if (audioElements[audioResult]) {
      source = audioElements[audioResult];
    } else {
      source = new Audio(audioResult);
    }

    // "playing" is the browser's true playback-start signal — it only fires
    // once audio frames are actually being rendered (after any buffering),
    // unlike calling .play() or a TTS fetch resolving. Callers that need to
    // know when sound genuinely starts (e.g. gating a lip-sync animation)
    // should hook onPlaying rather than the promise returned by addToQueue.
    // The browser also re-fires "playing" every time playback *resumes*
    // after a mid-clip stall/buffering pause, not just on the initial
    // start — { once: true } scopes this listener to this specific play()
    // call so onPlaying/highlightCallback only run once, for that first
    // start, rather than again on every later resume.
    source.addEventListener(
      "playing",
      () => {
        if (highlightCallback) {
          highlightCallback();
        }
        if (onPlaying) {
          onPlaying();
        }
      },
      { once: true },
    );

    source.addEventListener(
      "ended",
      () => {
        logger.debug("Audio clip ended");
        if (callback) {
          callback();
        }
      },
      false,
    );

    audioElements[audioResult] = source;

    try {
      await source.play();
    } catch (err) {
      logger.error(err);

      if (callback) {
        callback();
      }
    }
  };

  const playQueue = () => {
    logger.debug(`Checking Queue Size... [${queue.length}]`);

    if (queue[0] !== undefined) {
      const audio = queue[0];
      play(
        audio.binary,
        () => {
          logger.debug("Removing audio clip from queue...");
          queue.splice(0, 1);

          if (audio.callback) {
            logger.debug("Running Native Function Callback...");
            audio.callback();
          }
          if (queue.length > 0) {
            logger.debug("Playing next in queue...");
            playQueue();
          }
        },
        audio.highlightCallback,
        audio.onPlaying,
      );
    }
  };

  // eslint-disable-next-line no-unused-vars
  const stop = () => {
    if (source && source.pause) {
      source.pause();
      source.currentTime = 0;
    }
  };

  const clearQueue = () => {
    queue = [];
  };

  const muteSound = () => {
    if (source) {
      source.muted = true;
    }
  };

  const unmuteSound = () => {
    if (source) {
      source.muted = false;
    }
  };

  const toggleMuteSound = () => {
    if (source.muted === true) {
      unmuteSound();
    } else {
      muteSound();
    }
  };

  const addAudio = (binary, callback, highlightCallback, onPlaying) => {
    const audio = {};
    audio.binary = binary;
    audio.callback = callback;
    audio.highlightCallback = highlightCallback;
    audio.onPlaying = onPlaying;

    queue.push(audio);
    logger.debug(`Audio Added: ${queue.length}`);

    /*
     Check that there is at least one item in the queue and the audio isn't playing, 
     if there is more than one item it will be autoplayed by playQueue function 
     and we don't need to call playQueue again
    */
    if (queue.length > 0 && !isPlaying(source)) {
      playQueue();
    }
  };

  const addToQueue = (
    binary,
    callback,
    highlightCallback,
    isHoverText = false,
    onPlaying,
  ) => {
    if (!isHoverText || queue.length <= 0) {
      // if hovertext only speak if nothing in queue
      addAudio(binary, callback, highlightCallback, onPlaying);
    }
  };

  const addAudioListToQueue = (audioList = []) => {
    queue.push(...audioList);

    /*
     Check that there is at least one item in the queue and the audio isn't playing, 
     if there is more than one item it will be autoplayed by playQueue function 
     and we don't need to call playQueue again
    */
    if (queue.length > 0 && !isPlaying(source)) {
      playQueue();
    }
  };

  return { addToQueue, addAudioListToQueue, toggleMuteSound, clearQueue, stop };
}
